/**
 * Translation Applier - Automatically applies translations to elements with data-translate attribute
 * 
 * Scans DOM for elements with data-translate attribute and applies translations.
 * Works with TranslationRegistry system.
 */
(function(window) {
    'use strict';
    
    /**
     * Sanitize HTML to allow only safe tags
     * @param {string} html - HTML string to sanitize
     * @returns {string} - Sanitized HTML
     */
    function sanitizeHTML(html) {
        if (!html || typeof html !== 'string') return '';
        
        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // List of allowed tags and their allowed attributes
        const allowedTags = {
            'strong': [],
            'em': [],
            'b': [],
            'i': [],
            'u': [],
            'br': [],
            'span': ['class', 'style'],
            'a': ['href', 'target', 'rel', 'class'],
            'code': ['class'],
            'mark': [],
            'small': [],
            'sub': [],
            'sup': [],
            'p': ['class'],
            'div': ['class'],
            'ul': ['class'],
            'ol': ['class'],
            'li': ['class']
        };
        
        function sanitizeNode(node) {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.cloneNode(true);
            }
            
            if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();
                
                // If tag is not allowed, return only its text content
                if (!allowedTags[tagName]) {
                    const textNode = document.createTextNode(node.textContent);
                    return textNode;
                }
                
                // Create new element with allowed tag
                const newNode = document.createElement(tagName);
                
                // Copy allowed attributes
                const allowedAttrs = allowedTags[tagName];
                for (let i = 0; i < node.attributes.length; i++) {
                    const attr = node.attributes[i];
                    if (allowedAttrs.includes(attr.name.toLowerCase())) {
                        newNode.setAttribute(attr.name, attr.value);
                    }
                }
                
                // Recursively sanitize children
                for (let i = 0; i < node.childNodes.length; i++) {
                    const sanitizedChild = sanitizeNode(node.childNodes[i]);
                    if (sanitizedChild) {
                        newNode.appendChild(sanitizedChild);
                    }
                }
                
                return newNode;
            }
            
            return null;
        }
        
        // Sanitize all children
        const sanitizedDiv = document.createElement('div');
        for (let i = 0; i < tempDiv.childNodes.length; i++) {
            const sanitizedNode = sanitizeNode(tempDiv.childNodes[i]);
            if (sanitizedNode) {
                sanitizedDiv.appendChild(sanitizedNode);
            }
        }
        
        return sanitizedDiv.innerHTML;
    }
    
    const TranslationApplier = {
        /**
         * Apply translations to all elements with data-translate attribute
         * @param {string} lang - Optional language code (defaults to current language)
         */
        apply: function(lang = null) {
            if (!window.TranslationRegistry) {
                return;
            }
            
            // Get current language
            if (!lang) {
                if (window.LanguageManager && typeof window.LanguageManager.getCurrentLanguage === 'function') {
                    lang = window.LanguageManager.getCurrentLanguage();
                } else if (window.LANG) {
                    lang = window.LANG;
                } else {
                    lang = 'EN';
                }
            }
            // Translation data is keyed by UPPERCASE codes (EN/RU/DE/FR) per the
            // i18n contract. window.LANG comes from `{{ lang }}` in head as
            // lowercase (e.g. "ru"), and on pages without LanguageManager (e.g.
            // audio-lectures uses lang-pills-handler instead) the lowercase
            // value flows straight through. Without this normalisation lookups
            // miss → translate() falls back to EN, so RU/DE users see English
            // for every [data-translate*] element on the page.
            lang = String(lang).toUpperCase();
            
            // Find all elements with data-translate, data-translate-placeholder, data-translate-title, or data-translate-aria-label attribute
            const elements = document.querySelectorAll('[data-translate], [data-translate-placeholder], [data-translate-title], [data-translate-aria-label]');
            let appliedCount = 0;
            
            elements.forEach(function(element) {
                const key = element.getAttribute('data-translate');
                const placeholderKey = element.getAttribute('data-translate-placeholder');
                const titleKey = element.getAttribute('data-translate-title');
                const ariaLabelKey = element.getAttribute('data-translate-aria-label');
                
                // Handle placeholder translation separately
                if (placeholderKey) {
                    const placeholderTranslation = window.TranslationRegistry.translate(placeholderKey, lang);
                    if (placeholderTranslation !== placeholderKey && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')) {
                        element.placeholder = placeholderTranslation;
                        appliedCount++;
                    }
                }
                
                // Handle title translation
                if (titleKey) {
                    const titleTranslation = window.TranslationRegistry.translate(titleKey, lang);
                    if (titleTranslation !== titleKey) {
                        element.title = titleTranslation;
                        appliedCount++;
                    }
                }
                
                // Handle aria-label translation
                if (ariaLabelKey) {
                    const ariaLabelTranslation = window.TranslationRegistry.translate(ariaLabelKey, lang);
                    if (ariaLabelTranslation !== ariaLabelKey) {
                        element.setAttribute('aria-label', ariaLabelTranslation);
                        appliedCount++;
                    }
                }
                
                // Handle regular translation
                if (!key) return;
                
                // Check if element should use HTML (default: true, can be disabled with data-translate-html="false")
                const useHTML = element.getAttribute('data-translate-html') !== 'false';
                
                // Get translation
                const translation = window.TranslationRegistry.translate(key, lang);
                
                // Only update if translation was found (not the same as key)
                if (translation !== key) {
                    // Special handling for input/textarea elements (use placeholder or value)
                    // But only if data-translate-placeholder is not set (to avoid overriding)
                    if ((element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') && !placeholderKey) {
                        if (element.type === 'text' || element.type === 'email' || element.type === 'password' || element.type === 'search' || !element.type) {
                            element.placeholder = translation;
                        } else {
                            element.value = translation;
                        }
                    } else if (element.tagName === 'OPTION') {
                        // Handle OPTION elements - translate text content
                        element.textContent = translation;
                    } else if (useHTML && /<[^>]+>/.test(translation)) {
                        // Translation contains HTML tags, sanitize and use innerHTML
                        const sanitized = sanitizeHTML(translation);
                        element.innerHTML = sanitized;
                    } else {
                        // Plain text, use textContent
                        element.textContent = translation;
                    }
                    appliedCount++;
                }
            });
        },
        
        /**
         * Watch for new elements added to DOM and apply translations
         */
        watch: function() {
            // If called from inside <head> (e.g. inline registration in
            // _head_translations.html), document.body is still null and
            // observer.observe(null, …) throws TypeError. Defer to
            // DOMContentLoaded in that case so the observer attaches once the
            // body exists. Idempotency is fine — initApplier() also calls
            // watch(), and that path is guarded by `watchStarted`.
            if (!document.body) {
                document.addEventListener('DOMContentLoaded', function () {
                    TranslationApplier.watch();
                }, { once: true });
                return null;
            }
            // Use MutationObserver to watch for new elements
            const observer = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                    mutation.addedNodes.forEach(function(node) {
                        if (node.nodeType === 1) { // Element node
                            // Check if node itself has translation attributes
                            const hasTranslate = node.hasAttribute && node.hasAttribute('data-translate');
                            const hasPlaceholder = node.hasAttribute && node.hasAttribute('data-translate-placeholder');
                            const hasTitle = node.hasAttribute && node.hasAttribute('data-translate-title');
                            const hasAriaLabel = node.hasAttribute && node.hasAttribute('data-translate-aria-label');
                            
                            if (hasTranslate || hasPlaceholder || hasTitle || hasAriaLabel) {
                                // Uppercase to match the registry's UPPERCASE-keyed translation
                                // data (see apply() above for the full rationale).
                                const lang = String(
                                    window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN')
                                ).toUpperCase();
                                
                                // Handle placeholder translation
                                if (hasPlaceholder && window.TranslationRegistry) {
                                    const placeholderKey = node.getAttribute('data-translate-placeholder');
                                    if (node.tagName === 'INPUT' || node.tagName === 'TEXTAREA') {
                                        const placeholderTranslation = window.TranslationRegistry.translate(placeholderKey, lang);
                                        if (placeholderTranslation !== placeholderKey) {
                                            node.placeholder = placeholderTranslation;
                                        }
                                    }
                                }
                                
                                // Handle title translation
                                if (hasTitle && window.TranslationRegistry) {
                                    const titleKey = node.getAttribute('data-translate-title');
                                    const titleTranslation = window.TranslationRegistry.translate(titleKey, lang);
                                    if (titleTranslation !== titleKey) {
                                        node.title = titleTranslation;
                                    }
                                }
                                
                                // Handle aria-label translation
                                if (hasAriaLabel && window.TranslationRegistry) {
                                    const ariaLabelKey = node.getAttribute('data-translate-aria-label');
                                    const ariaLabelTranslation = window.TranslationRegistry.translate(ariaLabelKey, lang);
                                    if (ariaLabelTranslation !== ariaLabelKey) {
                                        node.setAttribute('aria-label', ariaLabelTranslation);
                                    }
                                }
                                
                                // Handle regular translation
                                if (hasTranslate && window.TranslationRegistry) {
                                    const key = node.getAttribute('data-translate');
                                    const useHTML = node.getAttribute('data-translate-html') !== 'false';
                                    
                                    const translation = window.TranslationRegistry.translate(key, lang);
                                    if (translation !== key) {
                                        // Only apply to placeholder if data-translate-placeholder is not set
                                        if ((node.tagName === 'INPUT' || node.tagName === 'TEXTAREA') && !hasPlaceholder) {
                                            if (node.type === 'text' || node.type === 'email' || node.type === 'password' || node.type === 'search' || !node.type) {
                                                node.placeholder = translation;
                                            } else {
                                                node.value = translation;
                                            }
                                        } else if (node.tagName === 'OPTION') {
                                            // Handle OPTION elements - translate text content
                                            node.textContent = translation;
                                        } else if (useHTML && /<[^>]+>/.test(translation)) {
                                            // HTML content
                                            const sanitized = sanitizeHTML(translation);
                                            node.innerHTML = sanitized;
                                        } else {
                                            // Plain text
                                            node.textContent = translation;
                                        }
                                    }
                                }
                            }
                            
                            // Check children
                            const children = node.querySelectorAll ? node.querySelectorAll('[data-translate], [data-translate-placeholder]') : [];
                            if (children.length > 0) {
                                TranslationApplier.apply();
                            }
                        }
                    });
                });
            });
            
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
            
            return observer;
        }
    };
    
    window.TranslationApplier = TranslationApplier;
    
    /**
     * Wait for translations to load and then apply
     */
    function waitAndApply() {
        // Check if TranslationRegistry is ready and has translations
        if (window.TranslationRegistry && window.TranslationRegistry._cache && Object.keys(window.TranslationRegistry._cache).length > 0) {
            TranslationApplier.apply();
            return true;
        }
        return false;
    }
    
    // Auto-apply on DOM ready
    let watchStarted = false;
    function initApplier() {
        // Try immediately
        if (waitAndApply()) {
            // Start watching for dynamically added elements
            if (!watchStarted && TranslationApplier.watch) {
                TranslationApplier.watch();
                watchStarted = true;
            }
            return;
        }
        
        // If not ready, wait a bit and try again (translations load asynchronously)
        let attempts = 0;
        const maxAttempts = 20; // 2 seconds max wait
        const interval = setInterval(function() {
            attempts++;
            if (waitAndApply() || attempts >= maxAttempts) {
                clearInterval(interval);
                // Start watching for dynamically added elements after translations are loaded
                if (!watchStarted && TranslationApplier.watch) {
                    TranslationApplier.watch();
                    watchStarted = true;
                }
                if (attempts >= maxAttempts) {
                }
            }
        }, 100);
    }
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initApplier);
    } else {
        initApplier();
    }
    
    // Also watch for language changes
    if (window.LanguageManager) {
        const originalSetLanguage = window.LanguageManager.setLanguage;
        if (originalSetLanguage) {
            window.LanguageManager.setLanguage = function(lang) {
                const result = originalSetLanguage.call(this, lang);
                setTimeout(function() {
                    TranslationApplier.apply(lang);
                }, 50);
                return result;
            };
        }
    }
})(window);

